import { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { colors } from "../theme";
import Header from "../components/Header";
import StepIndicator from "../components/StepIndicator";
import api from "../api";

// --- Types ---

interface Opponent {
  telegram_id: number;
  name: string;
  username?: string;
  id?: string;
  emoji?: string;
}

type BetMode = "challenge" | "dare" | "versus";

interface BetDraft {
  activity: string;
  target: string;
  deadline: string;
  amount: string;
  betType: BetMode;
  targetUser: Opponent | null;
  // Set when the user invited a @username that isn't on SideBets yet.
  // Used alongside a null targetUser so FundPage can create a bet with
  // just the username and open the Telegram share sheet.
  targetUsername: string | null;
}

// --- Activity Config ---

const ACTIVITIES = [
  { emoji: "\u{1F3C3}", label: "Run" },
  { emoji: "\u{1F6B4}", label: "Cycle" },
  { emoji: "\u{1F3CA}", label: "Swim" },
  { emoji: "\u{1F4AA}", label: "Workout" },
  { emoji: "\u{1F45F}", label: "Steps" },
  { emoji: "\u270D\uFE0F", label: "Custom" },
];

// --- Target Config (per activity) ---

const TARGET_CONFIG: Record<string, {
  question: string;
  sub: string;
  presets: string[];
  placeholder: string;
}> = {
  Run:     { question: "How far?",           sub: "Pick a distance",      presets: ["1 km", "3 km", "5 km", "10 km", "21 km"],                       placeholder: "e.g. 15 km by Friday" },
  Cycle:   { question: "How far?",           sub: "Pick a distance",      presets: ["10 km", "25 km", "50 km", "100 km"],                             placeholder: "e.g. 80 km this weekend" },
  Swim:    { question: "How far?",           sub: "Pick a distance",      presets: ["500 m", "1 km", "2 km", "5 km"],                                 placeholder: "e.g. 1.5 km in open water" },
  Workout: { question: "How many sessions?", sub: "Pick a goal",          presets: ["3 sessions", "5 sessions", "10 sessions", "20 sessions"],         placeholder: "e.g. 4 leg days this month" },
  Steps:   { question: "How many steps?",    sub: "Pick a daily target",  presets: ["5,000 steps", "10,000 steps", "15,000 steps", "20,000 steps"],   placeholder: "e.g. 12,000 steps for 7 days" },
  Custom:  { question: "What's the goal?",   sub: "Describe the challenge", presets: [],                                                              placeholder: "e.g. No junk food for a week" },
};

// --- Deadline Config ---

const DEADLINES = [
  { label: "\u23F0 24 hours", value: "24h",  hours: 24 },
  { label: "\u23F0 3 days",   value: "3d",   hours: 72 },
  { label: "\u23F0 1 week",   value: "1w",   hours: 168 },
  { label: "\u23F0 2 weeks",  value: "2w",   hours: 336 },
  { label: "\u23F0 1 month",  value: "1m",   hours: 720 },
];

// --- Amount Config ---
// Must stay in sync with backend/app/bot/handlers.py AMOUNT_PRESETS and
// MIN_AMOUNT_TON. Regression guard:
// backend/tests/test_amount_presets_contract.py (VEN-7).

const AMOUNTS = [
  { ton: "0.1", usd: "0.13" },
  { ton: "0.5", usd: "0.67" },
  { ton: "1.0", usd: "1.34" },
  { ton: "2.0", usd: "2.68" },
  { ton: "5.0", usd: "6.70" },
];

// --- Strava mapping ---

const STRAVA_MAP: Record<string, { type: string | null; unit: string | null }> = {
  Run:     { type: "distance", unit: "km" },
  Cycle:   { type: "distance", unit: "km" },
  Swim:    { type: "distance", unit: "m" },
  Workout: { type: "sessions", unit: "sessions" },
  Steps:   { type: "steps", unit: "steps" },
  Custom:  { type: null, unit: null },
};

// Try to extract a Strava-verifiable metric from free-text Custom input.
// This lets "run 10km" or "5000 steps" typed in the Custom field still get
// auto-verified via Strava instead of falling back to manual "BOTH AGREE".
function parseCustomToStrava(text: string): { type: string | null; value: number | null } {
  const t = text.toLowerCase();
  // Extract number + optional unit (e.g. "500m", "5km", "3 miles")
  const unitMatch = t.match(/(\d+(?:\.\d+)?)\s*(km|k|mi|miles?|m)?\b/);
  const rawValue = unitMatch ? parseFloat(unitMatch[1]) : null;
  const unit = unitMatch?.[2] || null;

  // Convert to km for distance values
  let value = rawValue;
  if (value !== null && unit === "m") {
    value = value / 1000; // meters → km
  } else if (value !== null && (unit === "mi" || unit === "mile" || unit === "miles")) {
    value = value * 1.609;
  }

  if (/\brun\b|\brunning\b|\bjog\b/.test(t)) return { type: "distance", value };
  if (/\bcycl|\bride\b|\bbik/.test(t)) return { type: "distance", value };
  if (/\bswim/.test(t)) return { type: "distance", value };
  if (/\bsteps?\b/.test(t)) return { type: "steps", value: rawValue }; // steps don't need conversion
  if (/\bworkout|\bsession|\bgym\b|\btraining\b/.test(t)) return { type: "sessions", value: rawValue };
  if (/\bwalk|\bhik/.test(t)) return { type: "distance", value };
  if (/\bkilomet|\bkm\b/.test(t) && value) return { type: "distance", value };
  if (/\bmile|\bmi\b/.test(t) && value) return { type: "distance", value };

  return { type: null, value: null };
}

// Extract the FIRST number from free-text target. Stripping all non-digits
// glued separate numbers together: "2500 steps for 1 day" became 25001.
export function parseTargetNumber(text: string): number | null {
  const match = text.replace(/,/g, "").match(/\d+(?:\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
}

// Derive deadline hours from DEADLINES array (single source of truth)

export default function CreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // If we were navigated here with a draft in state (e.g. back from /fund),
  // restore the user's work instead of starting from step 1.
  const restoredDraft = (location.state as { draft?: BetDraft } | null)?.draft;
  const [step, setStep] = useState<number>(() => {
    if (restoredDraft) {
      return restoredDraft.betType === "challenge" ? 5 : 6;
    }
    return 1;
  });
  const [draft, setDraft] = useState<BetDraft>(() =>
    restoredDraft ?? {
      activity: "",
      target: "",
      deadline: "",
      amount: "",
      betType: "challenge",
      targetUser: null,
      targetUsername: null,
    }
  );

  // Auto-advance: set value + go to next step after 180ms
  // Preset chips get a short delay for tactile feedback; custom "Continue" buttons advance immediately
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const autoAdvance = useCallback((update: Partial<BetDraft>, nextStep: number) => {
    setDraft((prev) => ({ ...prev, ...update }));
    advanceTimerRef.current = setTimeout(() => setStep(nextStep), 180);
  }, []);

  useEffect(() => {
    return () => { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); };
  }, []);

  const goToFund = (finalDraft: BetDraft) => {
    const desc = finalDraft.activity === "Custom"
      ? finalDraft.target
      : `${finalDraft.activity} ${finalDraft.target}`;
    const presetHours = DEADLINES.find((d) => d.value === finalDraft.deadline)?.hours;
    // VEN-45: for a custom date the deadline string is a local ISO like
    // "2026-04-15T12:00". We parse it as a real Date so we can ship the
    // exact timestamp to the backend (expires_at_iso) — `expires_in_hours`
    // alone rounds sub-hour precision into a fixed 3h expiry.
    const customDate = !presetHours ? new Date(finalDraft.deadline) : null;
    const customValid = customDate && !Number.isNaN(customDate.getTime()) && customDate.getTime() > Date.now();
    const rawCustomHours = customValid
      ? Math.ceil((customDate.getTime() - Date.now()) / (1000 * 60 * 60))
      : null;
    const expiresHours = presetHours
      || (rawCustomHours && rawCustomHours > 0 ? rawCustomHours : 168);
    const expiresAtIso = customValid ? customDate.toISOString() : null;
    const strava = STRAVA_MAP[finalDraft.activity] || { type: null, unit: null };
    const targetNum = parseTargetNumber(finalDraft.target);

    // For "Custom" activity, try to infer a Strava-trackable metric from the
    // free-text target so it auto-verifies instead of falling back to manual
    // "BOTH AGREE" resolution (VEN-18).
    let stravaType = strava.type;
    let stravaValue = targetNum;
    let isAutoVerifiable = finalDraft.activity !== "Custom";

    if (finalDraft.activity === "Custom" && !stravaType) {
      const parsed = parseCustomToStrava(finalDraft.target);
      if (parsed.type && parsed.value) {
        stravaType = parsed.type;
        stravaValue = parsed.value;
        isAutoVerifiable = true;
      }
    }

    // VEN-29: strava_target_value must always be null when strava_bet_type
    // is null. Otherwise a Custom bet like "10 pushups" would ship a
    // numeric target (10) without a bet type, confusing any backend logic
    // that inspects target_value without first checking bet_type.
    if (!stravaType) {
      stravaValue = null;
    }

    // VEN-56: when the Mini App was launched from a group via the bot's
    // attachment menu / keyboard WebApp button, Telegram puts the group
    // chat on initDataUnsafe. Forward it to the backend so the bet is
    // linked to that group and "BET IS LIVE" / "BET RESOLVED" group
    // cards (VEN-57 / VEN-58) have somewhere to post.
    //
    // VEN-56 round 2: regular `url` buttons in a /start group response
    // don't fill initDataUnsafe.chat at all (Telegram only does that for
    // attachment menu launches). handle_start_command now stamps the
    // group id into the startapp payload as `g{chatId}`, App.tsx writes
    // it to sessionStorage as a fallback. Use it when the live initData
    // doesn't have one.
    const tg = window.Telegram?.WebApp;
    const initChat = tg?.initDataUnsafe?.chat;
    const initGroupId = initChat?.id
      && (initChat.type === "group" || initChat.type === "supergroup")
      ? initChat.id
      : undefined;
    let telegramGroupId: number | undefined = initGroupId;
    if (telegramGroupId === undefined) {
      try {
        const stored = sessionStorage.getItem("pending_group_id");
        if (stored) {
          const parsed = parseInt(stored, 10);
          if (!Number.isNaN(parsed)) telegramGroupId = parsed;
        }
      } catch {
        // ignore — sessionStorage may be unavailable
      }
    }

    navigate("/fund", {
      state: {
        ...finalDraft,
        // VEN-44/55: description is the user's own wording. The deadline
        // belongs in its own field; appending " in 24h" polluted the string
        // that opponents see on the Accept screen and in group cards.
        description: desc,
        category: isAutoVerifiable ? "fitness" : "custom",
        expires_in_hours: expiresHours,
        expires_at_iso: expiresAtIso,
        strava_bet_type: stravaType,
        strava_target_value: stravaValue,
        // All three types are pile_on: challenge (self), dare (target), versus (head-to-head).
        bet_mode: "pile_on",
        pile_on_type: finalDraft.betType,
        target_telegram_id: finalDraft.targetUser?.telegram_id,
        targetUsername: finalDraft.targetUsername ?? undefined,
        telegram_group_id: telegramGroupId,
        // Carry the raw draft so FundPage's back button can restore the wizard.
        _fromCreate: true,
        _rawDraft: finalDraft,
      },
    });
  };

  const handleBack = () => {
    if (step === 6) setStep(5);
    else if (step > 1) setStep(step - 1);
    else navigate("/");
  };

  const handleClose = () => navigate("/");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Header title="NEW BET" showBack showClose onBack={handleBack} onClose={handleClose} />
      <StepIndicator current={step} total={6} />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          padding: "0 16px calc(24px + env(safe-area-inset-bottom))",
        }}
      >
        {step === 1 && <Step1Activity onSelect={(activity) => autoAdvance({ activity }, 2)} />}
        {step === 2 && (
          <Step2Target
            activity={draft.activity}
            onSelect={(target) => autoAdvance({ target }, 3)}
            onCustom={(target) => { setDraft((p) => ({ ...p, target })); setStep(3); }}
          />
        )}
        {step === 3 && (
          <Step3Deadline
            activity={draft.activity}
            target={draft.target}
            onSelect={(deadline) => autoAdvance({ deadline }, 4)}
            onCustom={(deadline) => { setDraft((p) => ({ ...p, deadline })); setStep(4); }}
          />
        )}
        {step === 4 && (
          <Step4Amount
            onSelect={(amount) => autoAdvance({ amount }, 5)}
            onCustom={(amount) => { setDraft((p) => ({ ...p, amount })); setStep(5); }}
          />
        )}
        {step === 5 && (
          <Step5BetType
            onSelect={(betType: BetMode) => {
              setDraft((p) => ({ ...p, betType }));
              if (betType === "challenge") {
                // Skip target picker, go to fund
                setTimeout(() => goToFund({ ...draft, betType, targetUser: null }), 180);
              } else {
                setTimeout(() => setStep(6), 180);
              }
            }}
          />
        )}
        {step === 6 && (
          <Step6PickTarget
            betType={draft.betType}
            onSelect={(targetUser: Opponent) => {
              const updated = { ...draft, targetUser, targetUsername: null };
              setDraft((p) => ({ ...p, targetUser, targetUsername: null }));
              setTimeout(() => goToFund(updated), 180);
            }}
            onInviteUsername={(username: string) => {
              const clean = username.trim().replace(/^@/, "");
              if (!clean) return;
              const updated = { ...draft, targetUser: null, targetUsername: clean };
              setDraft((p) => ({ ...p, targetUser: null, targetUsername: clean }));
              setTimeout(() => goToFund(updated), 180);
            }}
          />
        )}
      </div>
    </div>
  );
}


// ============================
// STEP 1: Activity
// ============================

function Step1Activity({ onSelect }: { onSelect: (activity: string) => void }) {
  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>
        What's the challenge?
      </div>
      <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 24 }}>
        Pick an activity
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
        }}
      >
        {ACTIVITIES.map(({ emoji, label }) => (
          <button
            key={label}
            onClick={() => onSelect(label)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: "20px 12px",
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 16,
              color: colors.white,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 32 }}>{emoji}</span>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}


// ============================
// STEP 2: Target
// ============================

function Step2Target({
  activity,
  onSelect,
  onCustom,
}: {
  activity: string;
  onSelect: (target: string) => void;
  onCustom: (target: string) => void;
}) {
  const config = TARGET_CONFIG[activity] || TARGET_CONFIG.Custom;
  const [customText, setCustomText] = useState("");

  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>
        {config.question}
      </div>
      <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 24 }}>
        {config.sub}
      </div>

      {/* Preset chips */}
      {config.presets.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 24 }}>
          {config.presets.map((preset) => (
            <button
              key={preset}
              onClick={() => onSelect(preset)}
              style={{
                padding: "12px 20px",
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 12,
                color: colors.white,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {preset}
            </button>
          ))}
        </div>
      )}

      {/* Custom input — always visible */}
      <div>
        {config.presets.length > 0 && (
          <div style={{ fontSize: 13, color: colors.gray, marginBottom: 10 }}>
            or set your own
          </div>
        )}
        <input
          type="text"
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          placeholder={config.placeholder}
          style={{
            width: "100%",
            padding: "14px 16px",
            background: colors.surfaceLight,
            border: `1px solid ${colors.borderLight}`,
            borderRadius: 12,
            color: colors.white,
            fontSize: 15,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {/* VEN-65: match the backend min_length=3 rule up front so users don't
            get a 400 when they tap Create & Fund with 1–2 characters. */}
        <div style={{ fontSize: 12, color: colors.gray, marginTop: 6 }}>
          {customText.trim().length > 0 && customText.trim().length < 3
            ? `${3 - customText.trim().length} more character${3 - customText.trim().length === 1 ? "" : "s"} needed`
            : "At least 3 characters"}
        </div>
        {customText.trim().length >= 3 && (
          <button
            onClick={() => onCustom(customText.trim())}
            style={{
              width: "100%",
              marginTop: 12,
              padding: "14px 0",
              background: colors.purple,
              border: "none",
              borderRadius: 12,
              color: colors.white,
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Continue with &apos;{customText.trim()}&apos; &rarr;
          </button>
        )}
      </div>
    </div>
  );
}


// ============================
// STEP 3: Deadline
// ============================

function Step3Deadline({
  activity,
  target,
  onSelect,
  onCustom,
}: {
  activity: string;
  target: string;
  onSelect: (deadline: string) => void;
  onCustom: (deadline: string) => void;
}) {
  const [customDate, setCustomDate] = useState("");
  // VEN-69: default to end-of-day so "Pick date → ✓ without changing" lands
  // on a valid future deadline (today 23:59) instead of today-noon, which
  // silently fell back to a 168h (1 week) expiry when evaluated in the evening.
  const [customTime, setCustomTime] = useState("23:59");
  const isFitness = activity !== "Custom";
  const activityEmoji = ACTIVITIES.find((a) => a.label === activity)?.emoji || "\u270D\uFE0F";

  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>
        When's the deadline?
      </div>
      <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 20 }}>
        Set a time limit
      </div>

      {/* Bet preview card */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 14,
          marginBottom: 24,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600 }}>
          {activityEmoji} {activity === "Custom" ? target : `${activity} ${target}`}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: isFitness ? colors.success : colors.grayLight,
            textTransform: "uppercase",
          }}
        >
          {isFitness ? "HEALTHBRIDGE AUTO \u2713" : "BOTH AGREE"}
        </span>
      </div>

      {/* Deadline presets */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
        {DEADLINES.map(({ label, value }) => (
          <button
            key={value}
            onClick={() => onSelect(value)}
            style={{
              padding: "14px 16px",
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              color: colors.white,
              fontSize: 15,
              fontWeight: 600,
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Custom date picker — iOS-style tiles */}
      <div>
        <div style={{ fontSize: 13, color: colors.gray, marginBottom: 10 }}>
          or set custom date & time
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 10,
          }}
        >
          <PickerTile
            icon={"\u{1F4C5}"}
            placeholder="Pick date"
            value={
              customDate
                ? new Date(`${customDate}T00:00:00`).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                : ""
            }
          >
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              min={new Date(Date.now() + 86400000).toISOString().split("T")[0]}
              style={pickerNativeInputStyle}
            />
          </PickerTile>
          <PickerTile icon={"\u23F0"} placeholder="Time" value={customTime || ""}>
            <input
              type="time"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              style={pickerNativeInputStyle}
            />
          </PickerTile>
        </div>
        {customDate && (() => {
          // VEN-69: guard against past deadlines. If native picker confirms a
          // past combination (e.g. today + an earlier time), block submission
          // so goToFund can't silently fall back to the 168h (1 week) default.
          const composed = `${customDate}T${customTime || "23:59"}`;
          const deadlineMs = new Date(composed).getTime();
          const isFuture = Number.isFinite(deadlineMs) && deadlineMs > Date.now();
          return (
            <button
              onClick={() => { if (isFuture) onCustom(composed); }}
              disabled={!isFuture}
              style={{
                width: "100%",
                marginTop: 12,
                padding: "14px 0",
                background: isFuture ? colors.purple : colors.surface,
                border: isFuture ? "none" : `1px solid ${colors.border}`,
                borderRadius: 12,
                color: isFuture ? colors.white : colors.grayLight,
                fontSize: 15,
                fontWeight: 700,
                cursor: isFuture ? "pointer" : "not-allowed",
              }}
            >
              {isFuture
                ? <>Set deadline: {new Date(customDate).toLocaleDateString()} &rarr;</>
                : "Pick a future date & time"}
            </button>
          );
        })()}
      </div>
    </div>
  );
}


// ============================
// STEP 4: Amount
// ============================

function Step4Amount({
  onSelect,
  onCustom,
}: {
  onSelect: (amount: string) => void;
  onCustom: (amount: string) => void;
}) {
  const [customAmount, setCustomAmount] = useState("");
  const [tonPrice, setTonPrice] = useState<number | null>(null);

  // Fetch TON price for fiat conversion
  useEffect(() => {
    api
      .get("/crypto/price/toncoin")
      .then((res) => {
        const price = res.data?.price || res.data?.toncoin?.usd;
        if (typeof price === "number") setTonPrice(price);
      })
      .catch(() => setTonPrice(1.34)); // fallback for dev
  }, []);

  const fiat = (ton: string) => {
    const rate = tonPrice || 1.34;
    return (parseFloat(ton) * rate).toFixed(2);
  };

  const customNum = parseFloat(customAmount);
  const isValid = !isNaN(customNum) && customNum >= 0.1;
  const tooLow = customAmount !== "" && !isNaN(customNum) && customNum < 0.1;

  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>
        How much?
      </div>
      <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 24 }}>
        Put your money where your mouth is
      </div>

      {/* Amount preset chips */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 10,
          marginBottom: 24,
        }}
      >
        {AMOUNTS.map(({ ton, usd }) => (
          <button
            key={ton}
            onClick={() => onSelect(ton)}
            style={{
              padding: "16px 12px",
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 14,
              color: colors.white,
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700 }}>{ton} TON</div>
            <div style={{ fontSize: 12, color: colors.grayLight, marginTop: 4 }}>
              &asymp; ${tonPrice ? fiat(ton) : usd}
            </div>
          </button>
        ))}
      </div>

      {/* Custom amount */}
      <div>
        <div style={{ fontSize: 13, color: colors.gray, marginBottom: 10 }}>
          or set custom
        </div>
        <div style={{ position: "relative" }}>
          <input
            type="number"
            inputMode="decimal"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            placeholder="0.0"
            step="0.1"
            min="0.1"
            style={{
              width: "100%",
              padding: "14px 60px 14px 16px",
              background: colors.surfaceLight,
              border: `1px solid ${tooLow ? colors.danger : colors.borderLight}`,
              borderRadius: 12,
              color: colors.white,
              fontSize: 15,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <span
            style={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: colors.gray,
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            TON
          </span>
        </div>

        {isValid && (
          <div style={{ fontSize: 13, color: colors.grayLight, marginTop: 6 }}>
            &asymp; ${fiat(customAmount)}
          </div>
        )}

        {tooLow && (
          <div style={{ fontSize: 13, color: colors.danger, marginTop: 6 }}>
            Minimum: 0.1 TON
          </div>
        )}

        {isValid && (
          <button
            onClick={() => onCustom(customAmount)}
            style={{
              width: "100%",
              marginTop: 12,
              padding: "14px 0",
              background: colors.purple,
              border: "none",
              borderRadius: 12,
              color: colors.white,
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Continue with {customAmount} TON &rarr;
          </button>
        )}
      </div>

      <div style={{ fontSize: 12, color: colors.gray, marginTop: 20, textAlign: "center" }}>
        Min: 0.1 TON &middot; Gas: ~0.02 TON
      </div>
    </div>
  );
}


// ============================
// STEP 5: Bet Type
// ============================

const BET_TYPES: { id: BetMode; emoji: string; title: string; desc: string; subtitle: string }[] = [
  { id: "challenge", emoji: "\uD83D\uDCAA", title: "Challenge", desc: "I'll do it myself", subtitle: "Others bet FOR or AGAINST you" },
  { id: "dare",      emoji: "\uD83C\uDFAF", title: "Dare",      desc: "I dare you to do it", subtitle: "Challenge a friend to prove themselves" },
  { id: "versus",    emoji: "\u2694\uFE0F",  title: "Versus",    desc: "I'll beat you", subtitle: "Head-to-head competition" },
];

function Step5BetType({ onSelect }: { onSelect: (type: BetMode) => void }) {
  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>
        What kind of bet?
      </div>
      <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 24 }}>
        Pick a bet type
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {BET_TYPES.map(({ id, emoji, title, desc, subtitle }) => (
          <button
            key={id}
            onClick={() => onSelect(id)}
            style={{
              width: "100%",
              padding: "16px",
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 16,
              color: colors.white,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: 28 }}>{emoji}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: 14, color: colors.grayLight }}>{desc}</div>
                <div style={{ fontSize: 12, color: colors.gray, marginTop: 4 }}>{subtitle}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}


// ============================
// STEP 6: Pick Target (Dare/Versus)
// ============================

interface InviteSuggestion {
  telegram_id: number;
  username: string;
  first_name?: string | null;
  name?: string | null;
}

interface ConfirmInviteState {
  username: string;
  suggestions: InviteSuggestion[];
}

function Step6PickTarget({
  betType,
  onSelect,
  onInviteUsername,
}: {
  betType: BetMode;
  onSelect: (user: Opponent) => void;
  onInviteUsername: (username: string) => void;
}) {
  const [friends, setFriends] = useState<Opponent[]>([]);
  const [searchResults, setSearchResults] = useState<Opponent[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [checkingInvite, setCheckingInvite] = useState(false);
  const [confirmInvite, setConfirmInvite] = useState<ConfirmInviteState | null>(null);

  // Load friends
  useEffect(() => {
    setLoading(true);
    api
      .get("/users/me/friends")
      .then((res) => {
        if (Array.isArray(res.data)) setFriends(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Search with debounce
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      setSearching(true);
      api
        .get(`/users/potential-opponents?search=${encodeURIComponent(searchQuery.trim())}`)
        .then((res) => {
          if (Array.isArray(res.data)) {
            // Show all matches, including existing friends — filtering them
            // out was hiding the exact person the user searched for.
            setSearchResults(res.data);
          }
        })
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Extract a "@username" candidate from the query so we can offer an invite
  // flow when nothing matched. Only treats it as a username if the query
  // looks like a plausible Telegram handle (letters/digits/underscores, 5+).
  const usernameCandidate = (() => {
    const raw = searchQuery.trim().replace(/^@/, "");
    if (raw.length < 5) return null;
    if (!/^[A-Za-z0-9_]+$/.test(raw)) return null;
    return raw;
  })();

  // Pre-flight: check a typed @username against the SideBets directory
  // before we let the creator lock their stake (VEN-X1).
  //   - exists=true  → promote to a direct pick (no share sheet, no 24h wait)
  //   - exists=false → open the confirm modal with "did you mean" suggestions
  //   - network error → fall back to the old invite-anyway flow so we never
  //     block the creator when the API is flaky
  const handleInviteTap = async (candidate: string) => {
    if (checkingInvite) return;
    setCheckingInvite(true);
    try {
      const res = await api.get("/users/check-username", { params: { username: candidate } });
      const data = res.data ?? {};
      if (data.exists && data.user?.telegram_id) {
        onSelect({
          telegram_id: data.user.telegram_id,
          name: data.user.name || data.user.first_name || data.user.username || candidate,
          username: data.user.username || candidate,
        });
        return;
      }
      setConfirmInvite({
        username: typeof data.username === "string" && data.username ? data.username : candidate,
        suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
      });
    } catch {
      onInviteUsername(candidate);
    } finally {
      setCheckingInvite(false);
    }
  };

  const title = betType === "dare" ? "Who do you dare?" : "Who do you challenge?";

  const renderPerson = (person: Opponent) => {
    const displayName = (person.name || "").replace(/^@/, "");
    const handle = person.username || displayName;
    return (
      <button
        key={person.telegram_id}
        onClick={() => onSelect(person)}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "12px 14px", background: "transparent",
          border: "none", borderRadius: 12, color: colors.white,
          cursor: "pointer", textAlign: "left", width: "100%",
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: 20, background: colors.purple,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, fontWeight: 700, flexShrink: 0,
        }}>
          {displayName.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{displayName}</div>
          <div style={{ fontSize: 13, color: colors.grayLight }}>@{handle}</div>
        </div>
      </button>
    );
  };

  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 16 }}>
        Pick a friend or search
      </div>

      <input
        type="text"
        placeholder="Search by name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%", padding: "12px 16px", borderRadius: 12,
          border: `1px solid ${colors.border}`, background: colors.surfaceLight,
          color: colors.white, fontSize: 15, marginBottom: 16,
          outline: "none", boxSizing: "border-box",
        }}
      />

      {searchQuery.trim().length >= 2 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: colors.grayLight, textTransform: "uppercase", marginBottom: 8, paddingLeft: 14 }}>
            Search results
          </div>
          {searching && <div style={{ fontSize: 14, color: colors.gray, textAlign: "center", padding: 16 }}>Searching...</div>}
          {!searching && searchResults.length === 0 && !usernameCandidate && (
            <div style={{ fontSize: 14, color: colors.gray, textAlign: "center", padding: 16, lineHeight: 1.5 }}>
              No matches. Type a full <b>@username</b> to send an invite via Telegram.
            </div>
          )}
          {searchResults.map(renderPerson)}
          {!searching && searchResults.length === 0 && usernameCandidate && (
            <button
              onClick={() => handleInviteTap(usernameCandidate)}
              disabled={checkingInvite}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "14px 16px", background: colors.surface,
                border: `1px dashed ${colors.primary}`, borderRadius: 12,
                color: colors.white, cursor: checkingInvite ? "wait" : "pointer",
                textAlign: "left", width: "100%", marginTop: 4,
                opacity: checkingInvite ? 0.7 : 1,
              }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 20, background: colors.primary,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 800, color: colors.bg, flexShrink: 0,
              }}>
                @
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>
                  {checkingInvite ? "Checking..." : `Invite @${usernameCandidate}`}
                </div>
                <div style={{ fontSize: 12, color: colors.grayLight, lineHeight: 1.4 }}>
                  Not on SideBets yet — we'll open Telegram to send them the challenge
                </div>
              </div>
            </button>
          )}
        </div>
      )}

      {searchQuery.trim().length < 2 && friends.length > 0 && (
        <div>
          <div style={{ fontSize: 12, color: colors.grayLight, textTransform: "uppercase", marginBottom: 8, paddingLeft: 14 }}>
            Recent opponents
          </div>
          {friends.map(renderPerson)}
        </div>
      )}

      {loading && <div style={{ fontSize: 14, color: colors.gray, textAlign: "center", padding: 32 }}>Loading...</div>}

      {!loading && friends.length === 0 && searchQuery.trim().length < 2 && (
        <div style={{ fontSize: 14, color: colors.gray, textAlign: "center", padding: 32, lineHeight: 1.5 }}>
          Type a name or @username above.<br />
          Or post <b>/dare @username</b> in any group chat.
        </div>
      )}

      {confirmInvite && (
        <div
          onClick={() => setConfirmInvite(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16, zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: colors.surface, borderRadius: 16, padding: 20,
              maxWidth: 420, width: "100%",
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
              @{confirmInvite.username} isn't on SideBets yet
            </div>
            <div style={{ fontSize: 13, color: colors.grayLight, marginBottom: 16, lineHeight: 1.5 }}>
              We'll send them a Telegram invite after you lock your stake.
              If they don't accept within 24h, your stake is automatically
              refunded.
            </div>

            {confirmInvite.suggestions.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 11, color: colors.grayLight, textTransform: "uppercase",
                  letterSpacing: 0.5, marginBottom: 8,
                }}>
                  Did you mean?
                </div>
                {confirmInvite.suggestions.map((s) => (
                  <button
                    key={s.telegram_id}
                    onClick={() => {
                      setConfirmInvite(null);
                      onSelect({
                        telegram_id: s.telegram_id,
                        name: s.name || s.first_name || s.username,
                        username: s.username,
                      });
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      width: "100%", padding: "10px 12px",
                      background: colors.surfaceLight, border: `1px solid ${colors.border}`,
                      borderRadius: 10, color: colors.white, cursor: "pointer",
                      marginBottom: 6, textAlign: "left",
                    }}
                  >
                    <span style={{
                      width: 32, height: 32, borderRadius: 16, background: colors.purple,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, fontWeight: 700, flexShrink: 0,
                    }}>
                      {(s.first_name || s.username || "?").charAt(0).toUpperCase()}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        {s.first_name || s.username}
                      </div>
                      <div style={{ fontSize: 12, color: colors.grayLight }}>
                        @{s.username}
                      </div>
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setConfirmInvite(null)}
                style={{
                  flex: 1, padding: "12px 0", background: "transparent",
                  border: `1px solid ${colors.border}`, borderRadius: 10,
                  color: colors.white, fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                Fix username
              </button>
              <button
                onClick={() => {
                  const u = confirmInvite.username;
                  setConfirmInvite(null);
                  onInviteUsername(u);
                }}
                style={{
                  flex: 1, padding: "12px 0", background: colors.primary,
                  border: "none", borderRadius: 10,
                  color: colors.bg, fontSize: 14, fontWeight: 700, cursor: "pointer",
                }}
              >
                Send invite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================
// PickerTile — iOS-style date/time tile with hidden native input overlay
// ============================

const pickerNativeInputStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  margin: 0,
  padding: 0,
  border: "none",
  background: "transparent",
  color: "transparent",
  opacity: 0,
  cursor: "pointer",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
  colorScheme: "dark",
};

function PickerTile({
  icon,
  placeholder,
  value,
  children,
}: {
  icon: string;
  placeholder: string;
  value: string;
  children: React.ReactNode;
}) {
  const hasValue = value.length > 0;
  return (
    <label
      style={{
        position: "relative",
        width: "100%",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 16px",
        background: colors.surfaceLight,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 12,
        color: colors.white,
        fontSize: 15,
        fontWeight: 600,
        cursor: "pointer",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: hasValue ? colors.white : colors.grayLight,
          fontWeight: hasValue ? 700 : 500,
        }}
      >
        {hasValue ? value : placeholder}
      </span>
      {children}
    </label>
  );
}
